// src/screens/Register/Register.js
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  updateProfile,
  onAuthStateChanged,
  signOut
} from "firebase/auth";
import { auth } from '../../firebase/firebase';
import { doc, setDoc, serverTimestamp, collection, addDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import './Register.css';

const Register = () => {
  const history = useHistory();

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    marketing: false
  });
  const [error, setError] = useState('');

  // toggles para ver/ocultar contraseña
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false);

  // Si ya estoy logueado, voy al home
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u && localStorage.getItem('isLoggedIn') === 'true') {
        history.push('/');
      }
    });
    return () => unsub();
  }, [history]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const { name, email, password, confirmPassword, marketing } = form;

    // Validaciones básicas
    if (!name || !email || !password || !confirmPassword) {
      setError('Todos los campos son obligatorios');
      return;
    }

    // Política de contraseña: 6+ chars, al menos 1 mayúscula y 1 número
    const policy = /^(?=.*[A-Z])(?=.*\d).{6,}$/;
    if (!policy.test(password)) {
      setError('La contraseña debe tener al menos 6 caracteres, incluir al menos una mayúscula y al menos un número.');
      return;
    }

    // Confirmación
    if (password !== confirmPassword) {
      setError('Las contraseñas no son iguales.');
      return;
    }

    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(user, { displayName: name });

      // Guarda el perfil + consentimiento en 'users/{uid}'
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email,
        emailLower: email.toLowerCase(),
        displayName: name,
        createdAt: serverTimestamp(),
        marketingConsent: !!marketing,
        consentAt: marketing ? serverTimestamp() : null,
        consentSource: 'register_form'
      });

      // Si marcó "Quiero recibir novedades", guardamos también en 'subscribers'
      if (marketing) {
        await addDoc(collection(db, 'subscribers'), {
          uid: user.uid,
          email,
          emailLower: email.toLowerCase(),
          name,
          consentAt: serverTimestamp(),
          source: 'register_form',
          topics: []
        });
      }

      // No persistimos sesión: salimos y mandamos a Login
      await signOut(auth);
      localStorage.removeItem('user');
      localStorage.removeItem('isLoggedIn');

      history.push('/login');
    } catch (err) {
      console.error(err);
      if (err?.code === 'auth/weak-password') {
        setError('La contraseña debe tener al menos 6 caracteres, incluir al menos una mayúscula y al menos un número.');
      } else if (err?.code === 'auth/email-already-in-use') {
        setError('Este email ya está registrado.');
      } else if (err?.code === 'auth/invalid-email') {
        setError('El email no tiene un formato válido.');
      } else {
        setError('Error al registrar usuario');
      }
    }
  };

  // Iconos SVG (ojo abierto / ojo cerrado)
  const EyeOpen = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7Zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10Z"/>
      <circle cx="12" cy="12" r="2.5"/>
    </svg>
  );

  const EyeClosed = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M2 4.27 3.28 3 21 20.72 19.73 22l-3.02-3.02C15.41 19.63 13.77 20 12 20c-5 0-9.27-3.11-11-7 1.02-2.3 2.86-4.26 5.13-5.58L2 4.27ZM12 8a4 4 0 0 1 4 4c0 .55-.11 1.08-.3 1.56L9.44 7.3C9.92 7.11 10.45 7 11 7h1Zm10 4c-.54 1.21-1.34 2.33-2.34 3.28l-1.43-1.43C19.18 13 19.64 12.52 20 12c-1.73-3.89-6-7-11-7-.86 0-1.7.09-2.5.27L5.82 4.59C7.68 3.86 9.78 3.5 12 3.5c5 0 9.27 3.11 11 7Z"/>
    </svg>
  );

  return (
    <div className="auth-container">
      <h1>Registrarse</h1>
      {error && <p className="auth-error">{error}</p>}

      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <label>Nombre y Apellido
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            autoComplete="name"
            placeholder="Ej: Juan Pérez"
            required
          />
        </label>

        <label>Email
          <input
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            autoComplete="email"
            placeholder="tu@email.com"
            required
          />
        </label>

        <label>Contraseña
          <div className="input-with-toggle">
            <input
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={handleChange}
              autoComplete="new-password"
              minLength={6}
              required
            />
            <button
              type="button"
              className="toggle-eye"
              onClick={() => setShowPassword(v => !v)}
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? <EyeOpen /> : <EyeClosed />}
            </button>
          </div>
          <small className="auth-hint">Mínimo 6 caracteres, al menos 1 mayúscula y 1 número.</small>
        </label>

        <label>Repetir contraseña
          <div className="input-with-toggle">
            <input
              name="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              value={form.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
              minLength={6}
              required
            />
            <button
              type="button"
              className="toggle-eye"
              onClick={() => setShowConfirm(v => !v)}
              aria-label={showConfirm ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              title={showConfirm ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showConfirm ? <EyeOpen /> : <EyeClosed />}
            </button>
          </div>
        </label>

        <label className="checkbox-line">
          <input
            type="checkbox"
            name="marketing"
            checked={form.marketing}
            onChange={handleChange}
          />
          Quiero recibir novedades y promociones por email. Podés darte de baja cuando quieras.
        </label>

        <button type="submit" className='auth-button'>Crear cuenta</button>
      </form>

      <p className="auth-switch">
        ¿Ya tenés cuenta?{' '}
        <span onClick={() => history.push('/login')} className="auth-link">
          Ingresá aquí
        </span>
      </p>
    </div>
  );
};

export default Register;
